import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from 'helpers/api'
import { IOrganisation, IOrganisationTeam, ITeamsListResponse } from 'types'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import Button from 'components/button'
import Skeleton from 'components/skeleton'
import { People, Users } from 'components/icons'
import CreateTeamModal from './createTeamModal'
import EditTeamModal from './editTeamModal'
import ProgressBar from 'components/progressBar'
import StatsCard from 'components/statsCard'
import InfoStrip from 'components/spreadsheet/components/infoStrip'

const TeamsOverview: React.FC = () => {
  const params: { organisationId?: string } = useParams()
  const navigate = useNavigate()
  const { displayErrorMessage, sidebarWidth } = useApplicationStore()
  const { user } = useAuth()

  const [loading, setLoading] = useState<boolean>(true)
  const [organisation, setOrganisation] = useState<IOrganisation>()
  const [teamsData, setTeamsData] = useState<ITeamsListResponse>()
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false)
  const [editingTeam, setEditingTeam] = useState<IOrganisationTeam | null>(null)

  useEffect(() => {
    document.title = 'Manage Teams'
  }, [])

  useEffect(() => {
    if (!params.organisationId) return
    api
      .getOrganisation(params.organisationId)
      .then((response) => setOrganisation(response.data))
      .catch(displayErrorMessage)
  }, [params.organisationId])

  const fetchTeams = () => {
    if (!params.organisationId) return
    api
      .getOrganisationTeams(params.organisationId)
      .then((response) => {
        setTeamsData(response.data)
      })
      .catch(displayErrorMessage)
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchTeams()
  }, [params.organisationId])

  const computeNonDefaultTeams = () => {
    if (!teamsData) return []
    return teamsData.teams.filter((t) => !t.isDefault)
  }
  const nonDefaultTeams = computeNonDefaultTeams()

  const computeDefaultTeam = () => {
    if (!teamsData) return null
    return teamsData.teams.find((t) => t.isDefault) || null
  }
  const defaultTeam = computeDefaultTeam()

  return (
    <div className="w-full p-5 flex flex-col gap-4 h-screen min-h-0">
      {/* Header */}
      <div className={`flex flex-wrap items-center gap-2 ${sidebarWidth === 0 ? 'ml-9' : ''}`}>
        <span onClick={() => navigate(-1)} className="text-muted hover:text-body transition-all text-sm cursor-pointer">
          &larr; Back to Dashboard
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {organisation ? (
          <h1 className="mr-auto">Teams — {organisation.name}</h1>
        ) : (
          <Skeleton className="h-[36px] w-[300px] mr-auto" />
        )}
        <Button internalType="accept" onClick={() => setCreateModalOpen(true)}>
          + Create Team
        </Button>
      </div>

      {/* Multi-year contract banner */}
      {organisation?.multiYearContract && (
        <div className="flex items-center gap-2 bg-really-light-blue border border-light-blue rounded p-3 text-sm">
          <span className="font-semibold">Flexible agreement active</span> — seat limit is a soft cap
        </div>
      )}

      {/* Summary stats */}
      {loading ? (
        <div className="grid grid-cols-4 max-lg:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[80px]" />
          ))}
        </div>
      ) : (
        teamsData && (
          <div className="grid grid-cols-4 max-lg:grid-cols-2 gap-4">
            <StatsCard label="Total Paid Seats" value={teamsData.totalSeats} />
            <StatsCard label="Allocated to Teams" value={teamsData.allocatedSeats} />
            <StatsCard label="Unallocated" value={defaultTeam ? `${defaultTeam.seatLimit}` : '—'} />
            <StatsCard label="Total Teams" value={teamsData.teams.length} />
          </div>
        )
      )}

      <InfoStrip
        title="How it works:"
        text="Allocate portions of the total paid user seats to teams.
          Each team has one or more managers who can assign/revoke paid user status within their seat limit - without
          needing org admin access."
      />

      {/* Team cards */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="grid grid-cols-3 max-lg:grid-cols-1 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[160px]" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 max-lg:grid-cols-1 gap-4">
            {/* Default team card */}
            {defaultTeam && (
              <div className="flex flex-col rounded border border-divider-subtle bg-surface p-4 shadow gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-[40px] h-[40px] text-blue">
                      <Users className="h-[18px] w-[18px]" />
                    </span>
                    <span className="font-bold text-lg">{defaultTeam.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-surface-muted text-muted px-2 py-1 rounded">Default</span>
                    <Button internalType="outline" onClick={() => setEditingTeam(defaultTeam)}>
                      Edit
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-muted">
                  {user?.isSuperAdmin
                    ? 'Unallocated seat pool. Super admins can edit the seat limit.'
                    : 'Users not assigned to a specific team. This team cannot be deleted.'}
                </div>
                <ProgressBar
                  percentage={defaultTeam.seatLimit > 0 ? (defaultTeam.seatsUsed / defaultTeam.seatLimit) * 100 : 0}
                  autoColour
                />
                <div className="text-sm text-muted">
                  {defaultTeam.seatsUsed} / {defaultTeam.seatLimit} seats used
                </div>
              </div>
            )}

            {/* Non-default team cards */}
            {nonDefaultTeams.map((team) => (
              <div
                key={team.publicId}
                className="flex flex-col rounded border border-divider-subtle bg-surface p-4 shadow gap-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-[40px] h-[40px] text-blue">
                      <People className="h-[18px] w-[18px]" />
                    </span>
                    <span className="font-bold text-lg">{team.name}</span>
                  </div>
                  <Button internalType="outline" onClick={() => setEditingTeam(team)}>
                    Edit
                  </Button>
                </div>
                <div className="text-sm text-muted">
                  {team.managers.length > 0
                    ? `Managers: ${team.managers.map((m) => `${m.name} (${m.email})`).join(', ')}`
                    : 'No managers assigned'}
                </div>
                <ProgressBar percentage={team.seatLimit > 0 ? (team.seatsUsed / team.seatLimit) * 100 : 0} autoColour />
                <div className="text-sm text-muted">
                  {team.seatsUsed} / {team.seatLimit} seats used
                  {team.pendingCount > 0 && ` · ${team.pendingCount} invited`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Team Modal */}
      {createModalOpen && organisation && (
        <CreateTeamModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          organisation={organisation}
          teamsData={teamsData}
          onCreated={fetchTeams}
        />
      )}

      {/* Edit Team Modal */}
      {editingTeam && organisation && (
        <EditTeamModal
          open={!!editingTeam}
          setOpen={(open) => {
            if (!open) setEditingTeam(null)
          }}
          organisation={organisation}
          team={editingTeam}
          teamsData={teamsData}
          onUpdated={fetchTeams}
        />
      )}
    </div>
  )
}

export default TeamsOverview
